import axios from 'axios';
import { EXCHANGE_BILL_URL } from '../constant/apiConstants.js';

/**
 * generateExchangeBill
 * ----------------------------------------------------------------------------
 * Sends an exchange bill to the backend and returns a normalized result.
 *
 * Builds two lists from the UI state:
 *   - itemReturnList   : the items being returned/exchanged from the original bill
 *   - itemPurchaseList : the new items being bought against the exchange credit
 *
 * printType is a QUERY param, not part of the body. The response format depends
 * on it:
 *   - THERMAL : JSON print payload        -> responseType 'json'
 *   - other (default NORMAL) : PDF bytes  -> responseType 'arraybuffer'
 *
 * @param {Object}   params
 * @param {number|string} params.billNo          The original bill being exchanged against.
 * @param {Array}    params.itemsToExchange       Items returned from the original bill.
 * @param {Array}    params.selectedItems         New items being purchased.
 * @param {Object}   params.user                  Current user (carries userId, storeId).
 * @param {string}  [params.printType='NORMAL']   'THERMAL' | 'NORMAL' | ... (case-insensitive).
 * @returns {Promise<{ response: import('axios').AxiosResponse, isThermal: boolean, printType: string }>}
 * @throws  Propagates any axios/network error to the caller.
 */
export const generateExchangeBill = async ({
  billNo,
  itemsToExchange,
  selectedItems,
  user,
  printType = 'NORMAL',
}) => {
  const storeId = user?.storeId || '';

  // Items being returned/exchanged from the original bill.
  const itemReturnList = itemsToExchange.map((item) => ({
    sno: item.sno,
    barcodedId: item.barcodedId,
    return_quantity: item.return_quantity,
    price: item.price,
    userId: user.userId,
  }));

  // New items purchased against the exchange credit.
  const itemPurchaseList = selectedItems.map((item) => {
    const isCustom = item.itemBarcodeID === 'SG9999999';
    return {
      barcodeId: item.itemBarcodeID,
      quantity: item.quantity,
      discount: item.discount || 0,
      // Custom items (SG9999999) carry a manually-entered price and their own
      // descriptive fields; regular items are resolved from inventory by
      // barcode, so those are sent as null.
      price: isCustom ? (item.price || 0) : null,
      description: isCustom ? (item.description || '') : null,
      itemCategory: isCustom ? (item.itemCategory || '') : null,
      itemType: isCustom ? (item.itemType || '') : null,
      size: isCustom ? (item.itemSize || '') : null,
      storeId: storeId,
    };
  });

  const requestData = {
    billToExchange: billNo,
    itemReturnList: itemReturnList,
    itemPurchaseList: itemPurchaseList,
    user: user,
  };

  // Normalize so 'thermal', ' Thermal ', 'THERMAL' all match.
  const normalizedPrintType = (printType || 'NORMAL').trim().toUpperCase();
  const isThermal = normalizedPrintType === 'THERMAL';

  // printType goes as a query param; the model is the body. Thermal -> JSON,
  // everything else -> raw PDF bytes.
  const response = await axios.post(
    `${EXCHANGE_BILL_URL}?printType=${normalizedPrintType}`,
    requestData,
    { responseType: isThermal ? 'json' : 'arraybuffer' }
  );

  return { response, isThermal, printType: normalizedPrintType };
};

export default generateExchangeBill;